<script lang="ts">
  import type { SurfaceSnapshot, SurfaceTask } from "../lib/types";
  import { completeTask } from "../lib/api";
  import { loadSnapshot } from "../lib/store";
  import { optimisticDone, markDone, clearDone, reconcile } from "../lib/optimistic";
  import StarButton from "../lib/StarButton.svelte";
  import SourceBadges from "../lib/SourceBadges.svelte";
  import FilterChips from "../lib/FilterChips.svelte";
  import FileSheet from "../lib/FileSheet.svelte";
  import { applyFilter, loadFilter, saveFilter, type Filter } from "../lib/filter";

  let { data }: { data: SurfaceSnapshot } = $props();

  let pending = $state<Set<string>>(new Set());
  let lastError = $state<string | null>(null);
  let filter = $state<Filter>(loadFilter("surface:filter:inbox"));
  let filingTask = $state<SurfaceTask | null>(null);

  function setFilter(f: Filter) {
    filter = f;
    saveFilter("surface:filter:inbox", f);
  }

  $effect(() => {
    const confirmed = data.tasks.filter((t) => t.state === "done").map((t) => t.fingerprint);
    if (confirmed.length > 0) reconcile(confirmed);
  });

  function isGtOnly(t: SurfaceTask): boolean {
    return t.fingerprint.startsWith("gt:");
  }

  function isInboxTask(t: SurfaceTask, opt: Record<string, unknown>): boolean {
    if (t.state !== "open") return false;
    if (t.fingerprint in opt) return false;
    if (isGtOnly(t)) return true;
    if (!t.project_slug) return true;
    if (t.project_slug.startsWith("_")) return true;
    return false;
  }

  function bucketOf(t: SurfaceTask): string {
    if (isGtOnly(t)) return "Unfiled · Google Tasks";
    if (t.project_slug?.startsWith("_")) {
      return t.project_slug.replace(/^_/, "").replace(/-/g, " ");
    }
    if (t.file && t.file.startsWith("01-Daily/")) {
      const date = t.file.replace("01-Daily/", "").replace(".md", "");
      return `Daily · ${date}`;
    }
    if (t.file && t.file.startsWith("03-Areas/")) {
      const m = t.file.match(/03-Areas\/([^\/]+)/);
      return `Area · ${m?.[1] ?? "?"}`;
    }
    return "Other";
  }

  function bucketRank(name: string): number {
    if (name === "Unfiled · Google Tasks") return 0;
    if (name === "inbox") return 1;
    if (name === "meeting followups" || name === "meeting-followups") return 2;
    if (name === "promotion queue") return 3;
    if (name.startsWith("Daily ·")) return 5;
    if (name.startsWith("Area ·")) return 6;
    return 4;
  }

  const inbox = $derived(
    data.tasks
      .filter((t) => isInboxTask(t, $optimisticDone))
      .filter((t) => applyFilter(t, filter)),
  );

  const grouped = $derived(() => {
    const buckets = new Map<string, SurfaceTask[]>();
    for (const t of inbox) {
      const b = bucketOf(t);
      if (!buckets.has(b)) buckets.set(b, []);
      buckets.get(b)!.push(t);
    }
    return [...buckets.entries()]
      .map(([name, items]) => ({ name, items }))
      .sort((a, b) => bucketRank(a.name) - bucketRank(b.name) || a.name.localeCompare(b.name));
  });

  function reportError(msg: string) {
    lastError = msg;
  }

  async function toggle(t: SurfaceTask) {
    if (pending.has(t.fingerprint)) return;
    lastError = null;
    pending = new Set([...pending, t.fingerprint]);
    markDone(t.fingerprint);
    try {
      await completeTask({
        file: t.file ?? "",
        line: t.line ?? 0,
        text: t.text,
        gt_account: t.account,
        gt_id: t.gt_id,
        target_state: "done",
      });
      void loadSnapshot({ fresh: true });
    } catch (e) {
      lastError = `Couldn't complete: ${e instanceof Error ? e.message : String(e)}`;
      clearDone(t.fingerprint);
    } finally {
      pending = new Set([...pending].filter((fp) => fp !== t.fingerprint));
    }
  }

  function openFile(t: SurfaceTask) {
    filingTask = t;
  }
  function closeFile() {
    filingTask = null;
  }
</script>

<div class="page">
  <header class="hero">
    <h1>Inbox</h1>
    <p class="subtitle">{inbox.length} unfiled · captures, follow-ups, GT-only items</p>
  </header>

  <FilterChips value={filter} onchange={setFilter} />

  {#if lastError}
    <div class="banner error">{lastError}</div>
  {/if}

  {#if inbox.length === 0}
    <p class="empty">Empty. Nothing waiting to be triaged.</p>
  {:else}
    {#each grouped() as g (g.name)}
      <section>
        <h2 class="section-title">{g.name}</h2>
        <ul class="grouped-list">
          {#each g.items as t (t.fingerprint)}
            <li class:pending={pending.has(t.fingerprint)}>
              <button class="check" type="button" aria-label="Mark done" disabled={pending.has(t.fingerprint)} onclick={() => toggle(t)}>
                <span class="checkbox"></span>
              </button>
              <span class="text">{t.text}</span>
              <span class="row-end">
                {#if isGtOnly(t)}
                  <button type="button" class="file-link" onclick={() => openFile(t)}>File…</button>
                {/if}
                <SourceBadges task={t} />
                <StarButton task={t} onerror={reportError} size="sm" />
              </span>
            </li>
          {/each}
        </ul>
      </section>
    {/each}
  {/if}
</div>

{#if filingTask}
  <FileSheet
    task={filingTask}
    projects={data.projects}
    onclose={closeFile}
    onerror={reportError}
  />
{/if}

<style>
  .page { padding: 8px 16px 140px; max-width: 720px; margin: 0 auto; }
  .hero { padding: 12px 4px 4px; }
  h1 {
    font-size: 34px;
    line-height: 1.05;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.025em;
  }
  .subtitle { color: var(--fg-dim); font-size: 15px; margin: 4px 0 12px; }

  .section-title {
    font-size: 13px;
    margin: 22px 16px 6px;
    color: var(--fg-dim);
    text-transform: capitalize;
    letter-spacing: 0.04em;
    font-weight: 500;
  }

  .empty { color: var(--fg-dim); padding: 20px 4px; font-size: 15px; }
  .banner.error {
    background: rgba(255, 69, 58, 0.14);
    color: #ff8a82;
    border: 1px solid rgba(255, 69, 58, 0.28);
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    margin-bottom: 12px;
  }

  .grouped-list {
    list-style: none;
    padding: 0;
    margin: 0;
    background: var(--bg-elev);
    border-radius: 12px;
    overflow: hidden;
  }
  .grouped-list li {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    padding: 10px 14px 10px 4px;
    border-bottom: 0.5px solid var(--separator);
    transition: opacity 0.2s, background 0.15s;
  }
  .grouped-list li:last-child { border-bottom: 0; }
  .grouped-list li.pending { opacity: 0.5; }

  .check { padding: 6px 10px; border-radius: 8px; flex-shrink: 0; }
  .check:active .checkbox { border-color: var(--accent); }
  .checkbox {
    display: block;
    width: 20px;
    height: 20px;
    border: 1.5px solid var(--fg-faint);
    border-radius: 50%;
    transition: border-color 0.15s, background 0.15s;
  }

  .text {
    flex: 1;
    min-width: 0;
    line-height: 1.32;
    padding-top: 7px;
    font-size: 16px;
    word-wrap: break-word;
  }
  .row-end { display: flex; align-items: center; gap: 6px; flex-shrink: 0; padding-top: 5px; }

  .file-link {
    font-size: 13px;
    font-weight: 500;
    color: var(--accent);
    padding: 5px 10px;
    border-radius: 8px;
  }
  .file-link:active { background: var(--tap); }
</style>
